/**
 * L'avviso: la risposta del sistema all'ultimo gesto.
 *
 * Una fascia sola per tutto il banco, perché è una cosa sola — «cosa è successo
 * quando ho toccato» — e sei copie in sei schermate avrebbero cominciato a
 * divergere alla prima parola cambiata. Stava in `flow-api.tsx`, che è il file
 * delle rotte di flusso: un componente di schermata lì dentro era un ospite, e
 * la ricezione infatti se n'era scritto uno suo.
 *
 * **Due modi, una decisione sola.** Senza `onChiudi` è quello di sempre: in
 * fondo alla schermata, nel flusso, sostituito dal gesto successivo. È quello
 * che vogliono le cinque schermate di reparto, dove sotto non c'è niente da
 * spingere. Con `onChiudi` galleggia sopra la pagina, porta la sua X e gli esiti
 * riusciti si tolgono da soli — serve dove la pagina è piena e ogni comparsa
 * sposterebbe la merce sotto le dita di chi conta.
 *
 * Le due cose vanno insieme di proposito: un avviso che copre la pagina **deve**
 * potersi togliere, o diventa un ostacolo; uno che sta nel flusso non ha niente
 * da cui togliersi.
 */

import { useEffect, useRef } from 'react';
import type { EsitoComando } from '@errebi/shared';

/**
 * Quanto resta a schermo un esito riuscito, prima di togliersi da solo.
 *
 * Cinque secondi: il tempo di alzare gli occhi dalla scatola e leggere due
 * righe, non uno di più.
 */
const DURATA_MS = 5000;

export function Avviso({
  esito,
  onChiudi,
}: {
  readonly esito: EsitoComando | null;
  /** C'è un modo di chiuderlo → l'avviso galleggia e porta la X. */
  readonly onChiudi?: () => void;
}) {
  /*
   * Un esito riuscito si toglie da solo; **un rifiuto no.**
   *
   * Sono due cose diverse. «Capo agganciato» è una conferma: serve un secondo,
   * poi ingombra, e al tocco dopo sarebbe comunque sostituita. Un rifiuto invece
   * è l'unica cosa che dice *perché* il banco ha detto di no, e chi lo legge può
   * avere le mani dentro una scatola: farlo sparire da solo è perderlo proprio
   * quando serviva.
   *
   * L'effetto sta prima del `return` anticipato perché un hook non può stare
   * dopo un ramo, e le dipendenze coprono il caso «due conferme di fila»: il
   * conto alla rovescia riparte, invece di portarsi dietro quello di prima.
   */
  const chiudi = useRef(onChiudi);
  // L'aggiornamento sta in un effetto e non nel corpo: scrivere una ref durante
  // il render è vietato, e React lo dice a voce alta (`react-hooks/refs`).
  // Questo effetto gira dopo ogni ridisegno, cioè molto prima che il timer
  // qui sotto abbia qualcosa da chiamare.
  useEffect(() => {
    chiudi.current = onChiudi;
  });

  useEffect(() => {
    if (esito === null || !esito.ok || chiudi.current === undefined) return;
    const t = setTimeout(() => chiudi.current?.(), DURATA_MS);
    return () => clearTimeout(t);
    // Dipende **solo dall'esito**, e la funzione passa da una ref.
    //
    // Chi ci monta sopra scrive `onChiudi={() => setUltimo(null)}`: una lambda
    // nuova a ogni render. Mettendola fra le dipendenze il conto alla rovescia
    // ripartiva da capo a ogni ridisegno — e al banco la vista si rilegge da
    // sola, quindi l'avviso non sarebbe sparito mai. Il test con i timer finti
    // non lo vedeva, perché lì fra un tick e l'altro non ridisegna nessuno.
  }, [esito]);

  if (!esito) return null;

  const sospeso = onChiudi !== undefined;
  return (
    <aside className={`avviso${esito.ok ? '' : ' avviso--no'}${sospeso ? ' avviso--sospeso' : ''}`}>
      <div className="avviso__testo">
        <b className="avviso__t">{esito.titolo}</b>
        <p className="avviso__m">{esito.ok ? esito.messaggio : esito.motivo}</p>
        {!esito.ok && esito.regola !== '—' && (
          <span className="avviso__r">regola {esito.regola} del prototipo</span>
        )}
      </div>
      {/* Il nome è dichiarato: «×» letto a voce non è un gesto, è un carattere. */}
      {onChiudi !== undefined && (
        <button className="avviso__x" aria-label="Chiudi l’avviso" onClick={onChiudi}>
          ×
        </button>
      )}
    </aside>
  );
}
